"use client";

import { Comedian, SpecialRow } from "@punchup/data/legacy";
import { useEffect, useMemo, useState } from "react";

import { VideoJS, VideoPlayerSurface } from "@punchup/video/client";
import { getCDNImageURL } from "@punchup/utils/getCDNImageURL";
import { getPublicVideoURL } from "@punchup/video/client";

export default function SpecialPreviewPlayer({
  special,
  comedian,
  muted = true,
  disableOverlays = false,
  showEndOverlay = true,
  showControls = false,
  onDurationChange,
  onTimeUpdate,
  onEnded,
  className,
}: {
  special: SpecialRow;
  comedian: Comedian;
  muted?: boolean;
  disableOverlays?: boolean;
  showEndOverlay?: boolean;
  // Opt-in playback controls (play/pause, seek, volume, fullscreen). Defaults to
  // false so the checkout trailer keeps its chrome-less look.
  showControls?: boolean;
  onDurationChange?: (durationSeconds: number) => void;
  onTimeUpdate?: (currentTime: number) => void;
  onEnded?: () => void;
  className?: string;
}) {
  const [videoSrc, setVideoSrc] = useState<string | null>(null);
  const [posterImg, setPosterImg] = useState<string | null>(null);

  useEffect(() => {
    const getVideoURL = async () => {
      if (special.preview_video_id) {
        const videoSrc = await getPublicVideoURL(special.preview_video_id);
        setVideoSrc(videoSrc);
      }
      if (special.preview_img) {
        const posterImg = getCDNImageURL(special.preview_img);
        setPosterImg(posterImg);
      }
    };
    getVideoURL();
  }, [special]);

  const stableContext = useMemo(
    () => ({
      recommendedVideos: [] as SpecialRow[],
      comedian,
      isPreview: true as const,
    }),
    [comedian]
  );

  if (!videoSrc || !posterImg) {
    return null;
  }

  return (
    <VideoJS
      muted={muted}
      inline
      src={videoSrc}
      poster={posterImg}
      lastPlaybackLocation={0}
      special={special}
      isPreview={true}
      surface={VideoPlayerSurface.ComedianCheckout}
      context={stableContext}
      onSpecialChange={async (special) => {}}
      disableOverlays={disableOverlays}
      showEndOverlay={showEndOverlay}
      disableControls={!showControls}
      disableLoop={true}
      onDurationChange={onDurationChange}
      onTimeUpdate={onTimeUpdate}
      onPlayStatusChanged={(status) => {
        if (status === "ENDED") onEnded?.();
      }}
      className={className ?? "overflow-hidden md:rounded-md"}
      playerProps={{
        showSkipButtons: false,
        showTimeRemaining: showControls,
        showPlaybackRate: false,
        showSubtitles: false,
        showVolumePanel: showControls,
        showFullscreenToggle: showControls,
      }}
    />
  );
}
